<script lang="ts">
	import Slider from '@smui/slider'
	import { orderFilterProducts, type ProductsEndpoint } from '$lib/js/products'
	import { getIntSearchParam, setSearchParam } from '$lib/js/helpers'
	import { storeActiveFilters, storeMaxPrice, storeSearchFilters } from '$lib/js/stores'
	import { page } from '$app/stores'
	import { t } from 'svelte-i18n-lingui'
	import type { BaseProduct, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import '$lib/css/components/input.scss'
	import { getContextScope } from '$lib/js/helpers/i18n'

	export let options: ProductQueryOptions
	export let products: (BaseProduct | FullProduct)[] = []
	export let type: string = '' // '' | search
	export let endpoint: ProductsEndpoint

	let maxPrice = type === 'search' ? ($storeSearchFilters.max_price || $storeMaxPrice) : parseInt(($page.data.max_price?.value * 1.21 + 1).toFixed(0))
	let priceMinFilter = type === 'search' ? 0 : getIntSearchParam($page.url, 'minPrice', 0)
	let priceMaxFilter = type === 'search' ? maxPrice : getIntSearchParam($page.url, 'maxPrice', maxPrice)
	const scope = getContextScope()

	/* Set default price filters */
	if (type !== 'search') {
		const minPriceParam = getIntSearchParam($page.url, 'minPrice', 0).toString()
		const maxPriceParam = getIntSearchParam($page.url, 'maxPrice', maxPrice).toString()
		if (minPriceParam) {
			$storeActiveFilters.min_price = parseInt(minPriceParam)
		}
		if (maxPriceParam) {
			$storeActiveFilters.max_price = parseInt(maxPriceParam)
		}
	}

	$: inputMin = priceMinFilter
	$: inputMax = priceMaxFilter

	async function orderFilteredProducts() {
		if (inputMin < priceMaxFilter) {
			priceMinFilter = inputMin
		}
		if (inputMax > priceMinFilter) {
			priceMaxFilter = inputMax
		}
		$storeActiveFilters.min_price = priceMinFilter
		$storeActiveFilters.max_price = priceMaxFilter

		if (!priceMinFilter) {
			options.min_price = 0
			priceMinFilter = 0
			$storeActiveFilters.min_price = 0
		} else {
			options.min_price = priceMinFilter
		}

		if (!priceMaxFilter) {
			options.max_price = maxPrice
			priceMaxFilter = maxPrice
			$storeActiveFilters.max_price = maxPrice
		} else {
			options.max_price = priceMaxFilter
		}

		products = await orderFilterProducts(options, endpoint, { scope: scope })

		if (type !== 'search') {
			setSearchParam('minPrice', $storeActiveFilters.min_price?.toString())
			setSearchParam('maxPrice', $storeActiveFilters.max_price?.toString())
		}
		options.start = options?.start ? options?.start + 50 : 50

		if (options.max_price !== $storeMaxPrice || options.min_price !== 0) {
			$storeActiveFilters.min_price = options.min_price
			$storeActiveFilters.max_price = options.max_price
		}

		document.getElementById('top-page')?.scrollIntoView({ behavior: 'smooth' })
	}

	$: if ($storeActiveFilters?.min_price === 0 && $storeActiveFilters?.max_price === maxPrice) {
		delete $storeActiveFilters.min_price
		delete $storeActiveFilters.max_price
		$storeActiveFilters = $storeActiveFilters

		priceMinFilter = 0
		priceMaxFilter = maxPrice
	}
</script>

<div class="price-filter">
	<div>
		<div class="slider">
			<Slider
				range min={0} max={maxPrice} step={1} input$aria-label="Range slider"
				bind:start={priceMinFilter}
				bind:end={priceMaxFilter}
				on:SMUISlider:change={() => orderFilteredProducts()}
			/>
		</div>
		<div class="price-inputs">
			<div class="price-input price-min">
				<label for="inputMin">{$t`Minimum price`}</label>
				<input
					id="inputMin"
					bind:value={inputMin}
					placeholder="0"
					type="number"
					on:input={() => orderFilteredProducts()}
				>
			</div>
			<div class="price-input price-max">
				<label for="inputMax">{$t`Maximum price`}</label>
				<input
					id="inputMax"
					bind:value={inputMax}
					placeholder={'' + $storeMaxPrice}
					type="number"
					on:input={() => { orderFilteredProducts() }}
				/>
			</div>
		</div>
	</div>
</div>

<style lang="scss">
	.slider {
		margin-left: -10px;
		margin-right: -10px;
		--mdc-theme-primary: var(--black); /* Change the primary color */
	}

	.price-filter {
		.price-inputs {
			display: flex;
			justify-content: space-between;
		}

		.price-input {
			background-color: var(--grey);
			border-radius: 5px;
			padding: 2px 10px;
			width: calc(50% - 26px);

			label {
				font-size: 12px;
				color: var(--grey--b);
			}

			input {
				border: none;
				background: none;
				padding: 0;
				font-size: 14px;
				width: 100%;

				@media (min-width: 1025px) {
					font-size: 16px;
				}

				&:focus {
					outline: none;
				}
			}
		}
	}
</style>
